import { ScrollTrigger, gsap } from "@client/gsap";
import { mediaQueries } from "@client/styles/media-queries";
import { useIsClient } from "@client/utils";
import { useGSAP } from "@gsap/react";
import React from "react";

import { ServicesModuleBlock } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { classNames } from "@thelabnyc/thelabui/src/utils/styles";

import { RichTextBlock } from "../RichText";

import styles from "./ServicesModule.module.scss";

export default function ServicesModules({
    value: props,
}: {
    value: ServicesModuleBlock;
}) {
    const root = React.useRef<HTMLDivElement>(null);
    const background = React.useRef<HTMLDivElement>(null);
    const leftContainer = React.useRef<HTMLDivElement>(null);
    const rightListWrapper = React.useRef<HTMLDivElement>(null);
    const isClient = useIsClient();
    const [activeSlide, setActiveSlide] = React.useState(0);

    useGSAP(() => {
        if (
            !root.current ||
            !leftContainer.current ||
            !rightListWrapper.current
        )
            return;

        const base = {
            trigger: root.current,
            start: "top bottom",
            end: "bottom bottom",
        };

        // Pin the left container for the full scroll duration
        ScrollTrigger.create({
            ...base,
            pin: leftContainer.current,
            pinSpacing: false,
        });

        // Calculate image transitions based on list content wrapper
        ScrollTrigger.create({
            trigger: rightListWrapper.current.querySelector("ul"),
            start: "top bottom",
            end: "bottom bottom",
            onUpdate: (self) => {
                if (!props.background) return;
                setActiveSlide(
                    Math.max(
                        0,
                        Math.min(
                            Math.floor(self.progress * props.background.length),
                            props.background.length - 1,
                        ),
                    ),
                );
            },
        });

        const parallax = {
            ...base,
            scrub: 0.1,
        };

        if (background.current) {
            gsap.fromTo(
                background.current,
                {
                    yPercent: 0,
                },
                {
                    yPercent: -90,
                    scrollTrigger: parallax,
                },
            );
        }
        gsap.fromTo(
            leftContainer.current.querySelector(`.${styles.left}`),
            {
                yPercent: 10,
            },
            {
                yPercent: -100,
                scrollTrigger: parallax,
            },
        );
    });

    return (
        <div className={styles.root}>
            <div className={styles.gridRoot} ref={root}>
                <div className={styles.leftContainer} ref={leftContainer}>
                    {props.background && (
                        <div className={styles.background} ref={background}>
                            {props.background.map(({ value, id }, i) => (
                                <picture
                                    key={id}
                                    className={classNames([
                                        // No transitions for first render so things don't fade out on first view
                                        [styles.animated, isClient],
                                        [styles.active, i == activeSlide],
                                    ])}
                                >
                                    {value.mobile_image?.url && (
                                        <source
                                            media={mediaQueries.belowMobile}
                                        />
                                    )}
                                    <img src={value.image?.url} />
                                </picture>
                            ))}
                        </div>
                    )}
                    {props.left && (
                        <RichTextBlock
                            largeHeadings
                            className={styles.left}
                            value={props.left}
                        />
                    )}
                </div>
                {props.right && (
                    <div ref={rightListWrapper} className={styles.rightWrapper}>
                        {props.left && (
                            <RichTextBlock
                                largeHeadings
                                className={styles.mobileLeft}
                                value={props.left}
                            />
                        )}
                        <StreamField
                            value={props.right}
                            attrs={{ className: styles.right }}
                            components={{
                                text: ({ value }) => <li>{value}</li>,
                            }}
                            tagName="ul"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
